// Task #2569 — la config Socket.IO (URL + transport/riconnessione) vive ora in
// `@easyflow/ui-frontend` (unica fonte di verita`, condivisa con il master).
// Qui restano solo: (a) l'override delicato per l'ambiente dev Replit, (b) la
// risoluzione dello slug tenant dal path (specifica del frontend tenant).
import {
  getSocketUrl as getSharedSocketUrl,
  getSocketConfig as getSharedSocketConfig,
  type SharedSocketConfig,
  type SocketTransport,
} from '@easyflow/ui-frontend';

export type { SharedSocketConfig, SocketTransport };

export const getSocketUrl = getSharedSocketUrl;

const REPLIT_DEV_HOST_SUFFIXES = ['.replit.dev', '.repl.co'] as const;

/**
 * Intervallo del probe readiness prima di (ri)aprire Socket.IO in dev Replit.
 * Il backend è avviato separatamente dal frontend e può restare in bootstrap
 * per qualche istante dopo una modifica: evitare tentativi Engine.IO in quella
 * finestra previene POST con sid ormai sconosciuto e il relativo rumore 400.
 */
export const DEV_SOCKET_READY_RETRY_MS = 2_000;

export function isReplitDevEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();
  return REPLIT_DEV_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

/**
 * Verifica il readiness reale del backend prima di collegare Socket.IO nel solo
 * dev Replit. In tutti gli altri ambienti il caller può procedere subito,
 * mantenendo invariata la semantica di produzione.
 */
export async function isSocketBackendReady(): Promise<boolean> {
  if (!isReplitDevEnvironment()) return true;

  try {
    const response = await fetch('/api/health/ready', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function getSocketConfig(): SharedSocketConfig {
  const base = getSharedSocketConfig();

  // In ambiente dev Replit (*.replit.dev / *.repl.co) il doppio hop di proxy
  // (proxy mTLS Replit -> Vite dev server -> backend) corrompe i frame
  // WebSocket dopo l'handshake HTTP iniziale, generando "Invalid frame
  // header" e tonnellate di reconnect. Il polling HTTP funziona perfettamente,
  // quindi in quell'unico ambiente disabilitiamo l'upgrade a WebSocket.
  if (!isReplitDevEnvironment()) return base;

  const pollingOnly: SocketTransport[] = ['polling'];
  // La riconnessione viene gestita dal SocketProvider dopo il readiness probe:
  // Socket.IO non deve emettere request polling durante il riavvio del backend.
  return {
    ...base,
    transports: pollingOnly,
    upgrade: false,
    reconnection: false,
  };
}

export function getTenantSlugFromPath(): string | undefined {
  const path = window.location.pathname;
  const match = path.match(/^\/([a-zA-Z0-9-]+)\//);
  return match?.[1];
}
